import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useDevices } from "../devices";
import DevicePicker from "../components/DevicePicker";
import { Award, Gauge, Route, AlertTriangle, RefreshCw, TrendingUp } from "lucide-react";

function fmtDateIST(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" });
}
function scoreColor(s) {
  if (s === null || s === undefined) return { bg: "bg-slate-100", text: "text-slate-400", ring: "stroke-slate-300" };
  if (s >= 85) return { bg: "bg-green-50", text: "text-moving", ring: "stroke-moving" };
  if (s >= 60) return { bg: "bg-amber-50", text: "text-alert", ring: "stroke-alert" };
  return { bg: "bg-red-50", text: "text-parked", ring: "stroke-parked" };
}

function ScoreRing({ value }) {
  const v = value ?? 0;
  const pct = Math.max(0, Math.min(100, v));
  const R = 38;
  const C = 2 * Math.PI * R;
  const col = scoreColor(value);
  return (
    <svg viewBox="0 0 100 100" className="w-24 h-24">
      <circle cx="50" cy="50" r={R} stroke="#e2e8f0" strokeWidth="8" fill="none" />
      <circle cx="50" cy="50" r={R}
              className={col.ring}
              strokeWidth="8" fill="none"
              strokeLinecap="round"
              strokeDasharray={`${C}`}
              strokeDashoffset={`${C - (C * pct) / 100}`}
              transform="rotate(-90 50 50)"
      />
      <text x="50" y="52" textAnchor="middle" dominantBaseline="middle"
            className={`font-black ${col.text}`} style={{ fontSize: 24 }}>
        {value ?? "—"}
      </text>
      <text x="50" y="68" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 9, fontWeight: 700 }}>
        /100
      </text>
    </svg>
  );
}

function ReportCard({ r, t, primary }) {
  const col = scoreColor(r.score);
  const empty = r.total_km === 0;
  return (
    <div data-testid={`report-${r.id}`} className={`border border-slate-200 rounded-2xl p-4 ${primary ? "shadow-soft" : ""}`}>
      <div className="flex items-center gap-4">
        <ScoreRing value={r.score} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
            {fmtDateIST(r.week_start)} – {fmtDateIST(new Date(new Date(r.week_end).getTime() - 1).toISOString())}
          </div>
          <div className={`font-black text-lg ${col.text}`}>
            {empty ? t.noDataWeek : (r.score >= 85 ? "Excellent" : r.score >= 60 ? "Good" : "Needs care")}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{t.generated}: {fmtDateIST(r.generated_at)}</div>
        </div>
      </div>
      {!empty && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Stat icon={<Route size={14}/>} label={t.totalDistance} value={`${r.total_km}`} suffix="km" />
          <Stat icon={<TrendingUp size={14}/>} label={t.tripsCount} value={`${r.trip_count}`} />
          <Stat icon={<AlertTriangle size={14}/>} label={t.overspeedEvents} value={`${r.overspeed_count}`} />
          <Stat icon={<Gauge size={14}/>} label={t.topSpeed} value={`${r.top_speed}`} suffix="km/h" />
        </div>
      )}
    </div>
  );
}
function Stat({ icon, label, value, suffix }) {
  return (
    <div className="bg-slate-50 rounded-xl px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{icon} {label}</div>
      <div className="text-slate-900 font-black text-base">
        {value}{suffix && <span className="text-[10px] text-slate-500 font-bold ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Reports() {
  const { t } = useAuth();
  const { selectedDeviceId } = useDevices();
  const deviceId = selectedDeviceId;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async (id) => {
    const { data } = await api.get(`/reports/${id}`);
    setReports(data);
  };
  useEffect(() => {
    if (!deviceId) { setReports([]); setLoading(false); return; }
    setLoading(true);
    (async () => {
      try { await load(deviceId); }
      finally { setLoading(false); }
    })();
  }, [deviceId]);

  const generate = async () => {
    if (!deviceId) return;
    setGenerating(true);
    try {
      await api.post("/reports/generate", { device_id: deviceId, week: "current" });
      await load(deviceId);
    } finally { setGenerating(false); }
  };

  const latest = reports[0];
  const history = reports.slice(1, 5);

  return (
    <div data-testid="reports-screen" className="min-h-[100dvh] bg-white pb-24">
      <div className="px-5 pt-6 pb-3 border-b border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-light text-primary flex items-center justify-center">
              <Award size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900">{t.weeklyReport}</h1>
              <p className="text-xs text-slate-500">{t.reportHistory}</p>
            </div>
          </div>
          <DevicePicker />
        </div>
      </div>

      <div className="px-5 mt-4 space-y-4">
        <button
          data-testid="reports-generate"
          onClick={generate}
          disabled={generating || !deviceId}
          className="w-full py-2.5 rounded-xl bg-primary-light text-primary font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <RefreshCw size={14} className={generating ? "animate-spin" : ""} />
          {generating ? t.loading : t.generateNow}
        </button>

        {loading ? (
          <div className="p-6 text-center text-slate-400">{t.loading}</div>
        ) : reports.length === 0 ? (
          <div data-testid="reports-empty" className="p-6 text-center text-slate-400">{t.noReportsYet}</div>
        ) : (
          <>
            <ReportCard r={latest} t={t} primary />
            {history.length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{t.reportHistory}</div>
                <div className="space-y-3">
                  {history.map((r) => (<ReportCard key={r.id} r={r} t={t} />))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
